import { format } from "date-fns";
import { Loader2, MessageCircle, Mic, Send, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import useApiRequest from "../../lib/hooks/useRequest";
import { ApplicantDetailsResponseDto, WhatsAppChatMessage, WhatsAppChatResponseDto } from "../../lib/interfaces/applicant";
import { cn } from "../../lib/utils/cn";
import { getInitials } from "../../lib/utils/initials";

const MEDIA_LABELS: Record<string, string> = {
  image: "📷 Bild",
  video: "🎥 Video",
  document: "📄 Dokument",
};

const MessageBody = ({ message }: { message: WhatsAppChatMessage }) => {
  if (message.kind === "audio") {
    return (
      <div className="flex items-start gap-2">
        <Mic className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
        <span className="whitespace-pre-wrap">{message.content ?? "Sprachnachricht"}</span>
      </div>
    );
  }
  if (message.kind === "image" || message.kind === "video" || message.kind === "document") {
    return (
      <div>
        <div className="font-medium text-slate-500">{MEDIA_LABELS[message.kind]}</div>
        {message.content && <div className="mt-0.5 whitespace-pre-wrap">{message.content}</div>}
      </div>
    );
  }
  // Templates/buttons carry their quick-reply buttons as [bracketed] text → render those as chips.
  if ((message.kind === "template" || message.kind === "button") && message.content) {
    const parts = message.content.split(/(\[[^\]]+\])/g);
    return (
      <span className="whitespace-pre-wrap">
        {parts.map((part, index) => {
          const match = part.match(/^\[([^\]]+)\]$/);
          return match ? (
            <span
              key={index}
              className="mx-0.5 my-0.5 inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 align-middle text-xs font-medium text-slate-600"
            >
              {match[1]}
            </span>
          ) : (
            <span key={index}>{part}</span>
          );
        })}
      </span>
    );
  }
  return <span className="whitespace-pre-wrap">{message.content ?? ""}</span>;
};

const WhatsAppChatPanel = ({
  item,
  isOpen,
  onClose,
  canSend,
  onCompose,
}: {
  item: ApplicantDetailsResponseDto;
  isOpen: boolean;
  onClose: () => void;
  canSend?: boolean;
  onCompose?: () => void;
}) => {
  const { apiRequest } = useApiRequest();
  const [messages, setMessages] = useState<WhatsAppChatMessage[]>();
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Live fetch on every open — the history is served straight from the AI, no caching.
  const loadChat = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<WhatsAppChatResponseDto>(`applicants/${item._id}/whatsapp-chat`, "GET");
    setMessages(response.data?.messages ?? []);
    setIsLoading(false);
  }, [apiRequest, item._id]);

  useEffect(() => {
    if (isOpen) {
      setMessages(undefined);
      loadChat();
    }
  }, [isOpen, loadChat]);

  useEffect(() => {
    if (messages?.length) {
      bottomRef.current?.scrollIntoView();
    }
  }, [messages]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  let lastDay = "";

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <aside className="relative z-10 flex h-full w-full max-w-[440px] flex-col bg-white shadow-2xl duration-200 animate-in slide-in-from-right">
        {/* Header */}
        <header className="flex items-center gap-3 border-b border-slate-200 px-5 py-4">
          <div className="relative flex-none">
            <span
              className="grid h-10 w-10 place-items-center rounded-full text-sm font-bold text-white"
              style={{ background: "linear-gradient(135deg,#4a52c9,#5a3ec8)" }}
            >
              {getInitials(item.name)}
            </span>
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-[#25d366]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-bold text-slate-900">{item.name}</div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <MessageCircle className="h-3.5 w-3.5 text-[#25d366]" />
              WhatsApp{item.phone ? ` · ${item.phone}` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4" style={{ background: "#f3f0f9" }}>
          {isLoading || !messages ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex h-full items-center justify-center text-center text-sm text-slate-500">
              Noch keine Nachrichten im Verlauf.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {messages.map((message, index) => {
                const date = new Date(message.createdAt);
                const validDate = !isNaN(date.getTime());
                const day = validDate ? format(date, "dd.MM.yyyy") : "";
                const showDay = day && day !== lastDay;
                if (showDay) lastDay = day;
                const isBot = message.role === "assistant";

                return (
                  <div key={index}>
                    {showDay && (
                      <div className="my-3 flex justify-center">
                        <span className="rounded-full bg-white/70 px-3 py-1 text-xs text-slate-500 shadow-sm">{day}</span>
                      </div>
                    )}
                    <div className={cn("flex", isBot ? "justify-end" : "justify-start")}>
                      <div
                        className={cn(
                          "max-w-[80%] rounded-2xl px-3 py-2 text-sm text-slate-800 shadow-sm",
                          isBot
                            ? "rounded-br-sm border border-[#cdeed8] bg-[#e7f9ee]"
                            : "rounded-bl-sm border border-slate-200 bg-white",
                        )}
                      >
                        <MessageBody message={message} />
                        {validDate && (
                          <div className="mt-1 text-right text-[10px] tabular-nums text-slate-400">
                            {format(date, "HH:mm")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* KI-Hinweis bleibt immer sichtbar; WRITE-User bekommen zusätzlich den Senden-Button. */}
        <footer className="border-t border-slate-200 px-4 py-3">
          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <Sparkles className="h-3.5 w-3.5 flex-none text-[#5257d6]" />
            Konversation wird von der WhatsApp KI geführt
          </div>
          {canSend && (
            <button
              type="button"
              onClick={onCompose}
              className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-full bg-[#25d366] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1fbe5a]"
            >
              <Send className="h-4 w-4" />
              Nachricht senden
            </button>
          )}
        </footer>
      </aside>
    </div>
  );
};

export default WhatsAppChatPanel;
